"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import Link from "next/link";
import { fadeUp, Section } from "./utils";

interface FAQProps {
  items?: { q: string; a: string }[];
}

export function FAQ({ items }: FAQProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const defaultFaqs = [
    {
      q: "How is this different from a regular chatbot?",
      a: "Most chatbots follow scripts. Tethra agents actually reason — they read your data, coordinate with each other, decide next steps, and escalate when they should. Think of it as hiring a team, not installing a widget.",
    },
    {
      q: "Will it work with the tools and channels we already use?",
      a: "Yes. Tethra connects to your existing channels — WhatsApp Business, email, CRMs, and more. Your clients see your brand, your tone, your number. They won't know it's AI unless you tell them.",
    },
    {
      q: "What if the AI says something wrong to a client?",
      a: "You set guardrails — what agents can and can't say, when to escalate to a human, and what approvals are needed for certain actions. Plus, every conversation is logged so you can review anything.",
    },
    {
      q: "How long does setup actually take?",
      a: "Most teams are live within an afternoon. Connect your channels, upload your data (listings, fleet info, service catalogs), set your pipeline stages, and you're running. No code required.",
    },
    {
      q: "What happens after the free trial?",
      a: "Plans start at $4.99/week. You get the full AI crew — sales, ops, and client success agents — with every plan. Scale up or down anytime. No contracts, no lock-in.",
    },
    {
      q: "Is our data secure?",
      a: "Your data is encrypted at rest and in transit. We're SOC 2 compliant and hosted on infrastructure in the UAE region. Your data is never used to train other models.",
    },
  ];

  const faqs = items ?? defaultFaqs;

  return (
    <Section className="px-6 py-28 max-w-3xl mx-auto">
      <motion.div variants={fadeUp} className="text-center mb-14">
        <p className="text-sm font-medium text-coral-400 mb-3 uppercase tracking-wider">
          Questions?
        </p>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-content">
          Answers to the things you&apos;re probably wondering.
        </h2>
      </motion.div>

      <div className="border border-surface-border rounded-2xl overflow-hidden bg-surface/60 divide-y divide-surface-border">
        {faqs.map((faq, i) => (
          <motion.div
            key={i}
            variants={fadeUp}
            custom={i * 0.3}
          >
            <button
              className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer hover:bg-surface-secondary/30 transition-colors"
              onClick={() => setOpenIndex(openIndex === i ? null : i)}
            >
              <span className="text-[15px] font-medium text-content pr-4">
                {faq.q}
              </span>
              <ChevronDown
                size={16}
                className={`text-content-muted shrink-0 transition-transform duration-300 ${
                  openIndex === i ? "rotate-180" : ""
                }`}
              />
            </button>
            {openIndex === i && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                transition={{ duration: 0.3 }}
                className="px-6 pb-4"
              >
                <p className="text-sm text-content-secondary leading-relaxed">
                  {faq.a}
                </p>
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>

      <motion.div
        variants={fadeUp}
        className="mt-10 rounded-xl border border-coral-500/30 bg-surface/60 p-7 text-center"
      >
        <p className="text-base font-semibold text-content mb-1">
          Need something specific to your business?
        </p>
        <p className="text-sm text-content-muted mb-5">
          Tell us what your team coordinates and we&apos;ll design an AI crew around it.
        </p>
        <Link
          href="/custom"
          className="group inline-flex items-center gap-1.5 text-sm font-medium text-coral-400 hover:text-coral-300 transition-colors"
        >
          Request a Custom Solution
          <ArrowRight
            size={14}
            className="group-hover:translate-x-0.5 transition-transform"
          />
        </Link>
      </motion.div>
    </Section>
  );
}
